import { useState } from 'react';
import { X, RefreshCw, CheckCircle, XCircle, Clock, Mail, FileText, Paperclip, Info, Download, AlertCircle } from 'lucide-react';
import Button from '../common/Button';
import Badge from '../common/Badge';
import emailsApi from '../../api/emails';
import { useUIStore } from '../../stores/uiStore';

/**
 * EmailDetailModal Component
 * Displays detailed information about a sent email with HTML preview and attachments
 */
const EmailDetailModal = ({ email, onClose, onRetrySuccess }) => {
  const [retrying, setRetrying] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const showToast = useUIStore(state => state.showToast);

  if (!email) return null;

  const tabs = [
    { id: 'preview', label: 'Email Preview', icon: FileText },
    { id: 'attachments', label: 'Attachments', icon: Paperclip, count: email.attachments?.length || 0 },
    { id: 'details', label: 'Details', icon: Info }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <Mail className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'success',
      failed: 'danger',
      pending: 'warning'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const getProviderBadge = (provider) => {
    const variants = {
      smtp: 'info',
      graph: 'primary'
    };
    return <Badge variant={variants[provider] || 'default'}>{provider?.toUpperCase()}</Badge>;
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      await emailsApi.retry(email.id);
      showToast('Email queued for retry', 'success');
      if (onRetrySuccess) {
        onRetrySuccess();
      }
      onClose();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to retry email', 'error');
    } finally {
      setRetrying(false);
    }
  };

  const handleDownloadAttachment = (attachmentId, filename) => {
    const downloadUrl = emailsApi.downloadAttachment(attachmentId);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(email.status)}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Email Details</h2>
              <p className="text-sm text-gray-500 mt-0.5">{email.subject}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Status Bar */}
        <div className="border-b px-6 py-3 flex items-center gap-4 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            {getStatusBadge(email.status)}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Provider:</span>
            {getProviderBadge(email.provider)}
          </div>
          {email.email_type && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Type:</span>
              <Badge variant="default">{email.email_type.replace(/_/g, ' ')}</Badge>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">Sent:</span>
            <span className="text-xs text-gray-700">{formatDate(email.sent_at)}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="px-6 flex gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Email Preview Tab */}
          {activeTab === 'preview' && (
            <div className="space-y-4">
              {email.email_body_html ? (
                <div>
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <div className="bg-gray-100 px-4 py-2 border-b">
                      <p className="text-xs font-medium text-gray-600">Email Preview</p>
                    </div>
                    <iframe
                      srcDoc={email.email_body_html}
                      className="w-full h-[500px] border-0"
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              ) : email.email_body_text ? (
                <div>
                  <label className="text-sm font-medium text-gray-700">Plain Text Email</label>
                  <pre className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm whitespace-pre-wrap">
                    {email.email_body_text}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No email content available</p>
                </div>
              )}
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-3">
              {email.attachments && email.attachments.length > 0 ? (
                email.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <Paperclip className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.original_filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.file_size)}
                          {attachment.mime_type && ` • ${attachment.mime_type}`}
                        </p>
                        {!attachment.file_exists && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertCircle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600">File no longer exists</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {attachment.file_exists && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Paperclip className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No attachments</p>
                </div>
              )}
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Email Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">To</label>
                  <p className="mt-1 text-gray-900">{email.recipient_email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">From</label>
                  <p className="mt-1 text-gray-900">{email.from_email || 'N/A'}</p>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <p className="mt-1 text-gray-900">{email.subject}</p>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Sent At</label>
                  <p className="mt-1 text-gray-600">{formatDate(email.sent_at)}</p>
                </div>
                {email.delivered_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Delivered At</label>
                    <p className="mt-1 text-gray-600">{formatDate(email.delivered_at)}</p>
                  </div>
                )}
              </div>

              {/* Entity Information */}
              {(email.entity_type || email.entity_id) && (
                <div className="grid grid-cols-2 gap-4">
                  {email.entity_type && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Entity Type</label>
                      <p className="mt-1 text-gray-900 capitalize">{email.entity_type}</p>
                    </div>
                  )}
                  {email.entity_id && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Entity ID</label>
                      <p className="mt-1 text-gray-900">{email.entity_id}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Retry Information */}
              {email.retry_count > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Retry Attempts</label>
                  <p className="mt-1 text-gray-900">{email.retry_count}</p>
                </div>
              )}

              {/* Message ID */}
              {email.message_id && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Message ID</label>
                  <p className="mt-1 text-gray-600 font-mono text-sm break-all">{email.message_id}</p>
                </div>
              )}

              {/* Error Message */}
              {email.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <label className="text-sm font-medium text-red-700">Error Message</label>
                  <p className="mt-1 text-red-900 font-mono text-sm">{email.error_message}</p>
                </div>
              )}

              {/* Metadata */}
              {email.metadata && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Additional Metadata</label>
                  <pre className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm overflow-x-auto">
                    {JSON.stringify(JSON.parse(email.metadata), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Email ID: {email.id}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {email.status === 'failed' && (
              <Button
                variant="primary"
                onClick={handleRetry}
                disabled={retrying}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
                {retrying ? 'Retrying...' : 'Retry Email'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailModal;